// Catálogo real (Etapa 1): categorias, subcategorias, produtos e variantes persistem no
// MongoDB via API FastAPI — os dados sobrevivem a logout, outro navegador e novos deploys.
// Este módulo mantém a MESMA interface pública da camada antiga de localStorage
// (getCustomProducts, addCustomProduct, saveProductOverride etc.) para não quebrar os
// consumidores existentes, mas agora lê de um cache em memória hidratado pelo servidor
// e grava via API. localStorage é usado apenas para a importação única de dados legados.
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT = 'hud-catalog-changed';
const LEGACY_KEY = 'hud_custom_products';
const IMPORTED_FLAG = 'hud_catalog_imported_v1';
const LOAD_TIMEOUT_MS = 6000;

let cache = { products: [], categories: [], subcategories: [], photoSettings: { allowProvisional: true } };
let ready = false;
let fallbackBuilder = null;
let initPromise = null;

// Registrado por data/catalog.js: catálogo base estático como reserva caso a API falhe.
export function registerCatalogFallback(builder) {
  fallbackBuilder = builder;
  if (!ready) cache = fallbackBuilder();
}

const notify = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
};

const adminRole = () => {
  try {
    return JSON.parse(localStorage.getItem('hud_admin_user') || 'null')?.role || '';
  } catch {
    return '';
  }
};

async function api(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': adminRole(), ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Falha ao salvar no servidor.');
  return data;
}

// Importação única (por navegador) dos dados que existiam no localStorage do protótipo.
// Idempotente no servidor (upsert por slug). Só roda com sessão de admin ativa.
async function importLegacyIfNeeded() {
  if (localStorage.getItem(IMPORTED_FLAG)) return;
  if (adminRole() !== 'admin') return;
  let legacy = null;
  try {
    legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || 'null');
  } catch {}
  if (!legacy) {
    localStorage.setItem(IMPORTED_FLAG, '1');
    return;
  }
  const hasContent =
    (legacy.products || []).length > 0 ||
    (legacy.categories || []).length > 0 ||
    (legacy.subcategories || []).length > 0 ||
    (legacy.deletedProducts || []).length > 0 ||
    Object.keys(legacy.variants || {}).length > 0 ||
    Object.keys(legacy.categoryOverrides || {}).length > 0 ||
    Object.keys(legacy.productOverrides || {}).length > 0 ||
    Object.keys(legacy.variantOverrides || {}).length > 0;
  if (!hasContent) {
    localStorage.setItem(IMPORTED_FLAG, '1');
    return;
  }
  await api('/api/catalog/import-legacy', { method: 'POST', body: JSON.stringify(legacy) });
  localStorage.setItem(IMPORTED_FLAG, '1');
  localStorage.removeItem(LEGACY_KEY);
}

async function loadCatalog() {
  const data = await api('/api/catalog');
  cache = {
    products: Array.isArray(data.products) ? data.products : [],
    categories: Array.isArray(data.categories) ? data.categories : [],
    subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
    photoSettings: data.photoSettings || { allowProvisional: true },
  };
}

// Hidrata o cache a partir do servidor. Chamado uma vez pelo App antes de renderizar.
export function initCatalog() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), LOAD_TIMEOUT_MS));
    try {
      await Promise.race([loadCatalog(), timeout]);
      try {
        await importLegacyIfNeeded();
        await loadCatalog();
      } catch (e) {
        console.error('Importação de dados locais do protótipo falhou:', e);
      }
    } catch (e) {
      console.error('Catálogo: API indisponível, usando base local estática.', e);
      if (fallbackBuilder) cache = fallbackBuilder();
    }
    ready = true;
    notify();
  })();
  return initPromise;
}

export const isCatalogReady = () => ready;

// Recarrega o catálogo do servidor sob demanda (ex.: após envio de foto pelo vendedor,
// que não usa as rotas de escrita de admin para atualizar o cache local).
export async function refreshCatalog() {
  await loadCatalog();
  notify();
}

export function useCatalogInit() {
  const [isReady, setIsReady] = useState(ready);
  useEffect(() => {
    let alive = true;
    initCatalog().then(() => alive && setIsReady(true));
    return () => {
      alive = false;
    };
  }, []);
  return isReady;
}

export function useCatalogTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);
}

// ---- Leituras (síncronas, do cache hidratado pelo servidor) ----
export const getAllCatalogProducts = () => cache.products;
export const getAllCatalogCategories = () => cache.categories;
export const getAllCatalogSubcategories = () => cache.subcategories;
// Preferência pública de imagem provisória (servidor, coleção catalog_config).
export const getCatalogPhotoSettings = () => cache.photoSettings;
export function setCatalogPhotoSettings(patch) {
  cache = { ...cache, photoSettings: { ...cache.photoSettings, ...patch } };
  notify();
}

export const getCustomProducts = () => cache.products.filter((p) => p.custom);
export const getCustomCategories = () => cache.categories.filter((c) => c.custom);
export const getCustomSubcategories = () => cache.subcategories;
// Buckets antigos não existem mais no modelo servidor (edições são aplicadas direto nos documentos).
export const getAddedVariants = () => ({});
export const getCategoryOverrides = () => ({});
export const getVariantOverrides = () => ({});
export const getProductOverrides = () => ({});
export const isProductDeleted = () => false;

// ---- Escritas (assíncronas, via API; cache local atualizado após sucesso) ----
export async function addCustomProduct(product) {
  const doc = await api('/api/catalog/products', { method: 'POST', body: JSON.stringify(product) });
  cache = { ...cache, products: [...cache.products, doc] };
  notify();
}

export async function updateCustomProduct(slug, patch) {
  await api(`/api/catalog/products/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify(patch) });
  cache = {
    ...cache,
    products: cache.products.map((p) => (p.slug === slug ? { ...p, ...patch, slug: p.slug } : p)),
  };
  notify();
}

// Edições antigas de produto viraram PATCH direto no documento (servidor é a fonte única).
export const saveProductOverride = updateCustomProduct;

export async function deleteProduct(slug) {
  await api(`/api/catalog/products/${encodeURIComponent(slug)}`, { method: 'DELETE' });
  cache = { ...cache, products: cache.products.filter((p) => p.slug !== slug) };
  notify();
}

export async function addVariantToFabric(slug, variant) {
  const doc = await api(`/api/catalog/products/${encodeURIComponent(slug)}/variants`, {
    method: 'POST',
    body: JSON.stringify(variant),
  });
  cache = {
    ...cache,
    products: cache.products.map((p) => (p.slug === slug ? { ...p, variants: [...p.variants, doc] } : p)),
  };
  notify();
}

export async function patchCustomVariant(slug, variantId, patch) {
  await api(`/api/catalog/products/${encodeURIComponent(slug)}/variants/${encodeURIComponent(variantId)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  cache = {
    ...cache,
    products: cache.products.map((p) =>
      p.slug === slug
        ? { ...p, variants: p.variants.map((v) => (v.id === variantId ? { ...v, ...patch } : v)) }
        : p
    ),
  };
  notify();
}

export const setVariantOverride = patchCustomVariant;

export async function removeVariant(slug, variantId) {
  await api(`/api/catalog/products/${encodeURIComponent(slug)}/variants/${encodeURIComponent(variantId)}`, {
    method: 'DELETE',
  });
  cache = {
    ...cache,
    products: cache.products.map((p) =>
      p.slug === slug ? { ...p, variants: p.variants.filter((v) => v.id !== variantId) } : p
    ),
  };
  notify();
}

// Categorias são registros normais no servidor; esta função mantém o nome antigo
// apenas para compatibilidade com os formulários existentes.
export async function addCustomCategory(cat) {
  const doc = await api('/api/catalog/categories', { method: 'POST', body: JSON.stringify(cat) });
  cache = { ...cache, categories: [...cache.categories, doc] };
  notify();
}

export async function updateCustomCategory(slug, patch) {
  await api(`/api/catalog/categories/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify(patch) });
  cache = {
    ...cache,
    categories: cache.categories.map((c) => (c.slug === slug ? { ...c, ...patch } : c)),
  };
  notify();
}

// Edição de qualquer categoria: mesma operação (o slug nunca muda).
export const saveCategoryOverride = updateCustomCategory;

export async function removeCustomCategory(slug, options = {}) {
  const params = new URLSearchParams();
  if (options.mode) params.set('mode', options.mode);
  if (options.targetCategorySlug) params.set('targetCategorySlug', options.targetCategorySlug);
  const qs = params.toString();
  const result = await api(`/api/catalog/categories/${encodeURIComponent(slug)}${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
  await loadCatalog();
  notify();
  return result;
}


export async function addSubcategory(sub) {
  const doc = await api('/api/catalog/subcategories', { method: 'POST', body: JSON.stringify(sub) });
  cache = { ...cache, subcategories: [...cache.subcategories, doc] };
  notify();
}

export async function updateSubcategory(slug, patch) {
  await api(`/api/catalog/subcategories/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify(patch) });
  cache = {
    ...cache,
    subcategories: cache.subcategories.map((s) => (s.slug === slug ? { ...s, ...patch, slug: s.slug } : s)),
  };
  notify();
}

export async function removeSubcategory(slug) {
  await api(`/api/catalog/subcategories/${encodeURIComponent(slug)}`, { method: 'DELETE' });
  cache = { ...cache, subcategories: cache.subcategories.filter((s) => s.slug !== slug) };
  notify();
}

export const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
